import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

type QuoteTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  maxHeight?: number;
  /** Keep the rows-defined height and use internal scrolling. */
  fixedHeight?: boolean;
  /**
   * Literal snippets (e.g. `@Some Name` mentions) tinted in place so the
   * author can see what the composer recognized. Purely visual, like the
   * quote presentation: the textarea value is untouched.
   */
  highlightTokens?: string[];
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const mergeRefs = (
  forwarded: React.ForwardedRef<HTMLTextAreaElement>,
  node: HTMLTextAreaElement | null,
) => {
  if (typeof forwarded === "function") forwarded(node);
  else if (forwarded) forwarded.current = node;
};

/**
 * A textarea that keeps Markdown as its real value while presenting leading
 * `>` lines as a familiar quoted-reply block. The transparent textarea remains
 * the only editable control; the overlaid text is purely visual.
 */
export const QuoteTextarea = React.forwardRef<HTMLTextAreaElement, QuoteTextareaProps>(
  (
    {
      value = "",
      className = "",
      maxHeight = 160,
      fixedHeight = false,
      highlightTokens,
      onScroll,
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const quoteLayerRef = useRef<HTMLDivElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [manualHeight, setManualHeight] = useState<number | null>(null);
    const manualTextRef = useRef<string | null>(null);
    const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
    const text = String(value);
    const hasQuote = text.split(/\r?\n/).some(line => /^> ?/.test(line));

    // One alternation with the capture kept by split(), longest token first so
    // a token extending another is never half-matched. The lookahead stops a
    // token from matching inside a longer word.
    const tokenPattern = useMemo(() => {
      const tokens = (highlightTokens ?? []).filter(token => token.length > 0);
      if (tokens.length === 0) return null;
      const alternation = [...tokens]
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
        .join("|");
      return new RegExp(`((?:${alternation})(?![\\w-]))`);
    }, [highlightTokens]);
    const hasHighlight = tokenPattern?.test(text) ?? false;
    const overlayActive = hasQuote || hasHighlight;

    /** Interleaves plain runs with tinted token spans; identical metrics keep the overlay aligned. */
    const withHighlights = (part: string) =>
      tokenPattern
        ? part.split(tokenPattern).map((piece, index) =>
            index % 2 === 1 ? (
              <span key={index} className="text-primary underline underline-offset-2">
                {piece}
              </span>
            ) : (
              piece
            ),
          )
        : part;

    const heightLimits = () => {
      const input = inputRef.current;
      const computed = input ? window.getComputedStyle(input) : null;
      const cssMinimum = computed ? Number.parseFloat(computed.minHeight) : 0;
      const lineHeight = computed ? Number.parseFloat(computed.lineHeight) : 20;
      const verticalPadding = computed
        ? Number.parseFloat(computed.paddingTop) + Number.parseFloat(computed.paddingBottom)
        : 16;
      return {
        // `rows` defines the comfortable automatic/default height. Manual
        // resizing may intentionally compact the composer to a single line.
        min: Math.max(cssMinimum || 0, (lineHeight || 20) + verticalPadding),
        max: Math.max(maxHeight, Math.floor(window.innerHeight * 0.65)),
      };
    };

    useLayoutEffect(() => {
      const input = inputRef.current;
      if (!input) return;
      if (fixedHeight) {
        input.style.height = "";
        input.style.overflowY = input.scrollHeight > input.clientHeight ? "auto" : "hidden";
        if (quoteLayerRef.current) {
          quoteLayerRef.current.style.height = `${Math.max(0, input.clientHeight - 2)}px`;
        }
        return;
      }
      input.style.height = "auto";
      const contentHeight = Math.min(input.scrollHeight, maxHeight);
      // A manual height is a preference, not permission for new lines to
      // escape the editor. Typing or inserting a quote grows the box enough to
      // reveal its content, up to the normal automatic cap.
      const manuallySizedForCurrentText = manualTextRef.current === text;
      const height =
        manualHeight === null
          ? contentHeight
          : manuallySizedForCurrentText
            ? manualHeight
            : Math.max(manualHeight, contentHeight);
      input.style.height = `${height}px`;
      if (quoteLayerRef.current) {
        quoteLayerRef.current.style.height = `${Math.max(0, input.clientHeight - 2)}px`;
      }
      const overflows = input.scrollHeight > height;
      input.style.overflowY = overflows ? "auto" : "hidden";
      if (!overflows && input.scrollTop !== 0) {
        input.scrollTop = 0;
        setScrollTop(0);
      }
    }, [text, maxHeight, manualHeight, fixedHeight]);

    useEffect(() => {
      const move = (event: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const { min, max } = heightLimits();
        setManualHeight(
          Math.min(max, Math.max(min, drag.startHeight + drag.startY - event.clientY)),
        );
      };
      const finish = () => {
        if (!dragRef.current) return;
        dragRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", finish);
      window.addEventListener("pointercancel", finish);
      return () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", finish);
        finish();
      };
      // The listeners must remain mounted for the whole gesture. Re-running
      // this effect after setManualHeight would invoke `finish` during cleanup
      // and stop the drag after its first pointermove.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resizeBy = (delta: number) => {
      const input = inputRef.current;
      if (!input) return;
      const { min, max } = heightLimits();
      manualTextRef.current = text;
      setManualHeight(current =>
        Math.min(max, Math.max(min, (current ?? input.offsetHeight) + delta)),
      );
    };

    return (
      <div className="relative flex-1">
        {!fixedHeight && (
          <div
            role="separator"
            aria-label="Resize message box"
            aria-orientation="horizontal"
            tabIndex={0}
            onPointerDown={event => {
              const input = inputRef.current;
              if (!input) return;
              event.preventDefault();
              manualTextRef.current = text;
              // Adopt the rendered (possibly auto-expanded) height as the start
              // of this manual gesture before applying pointer deltas.
              setManualHeight(input.offsetHeight);
              dragRef.current = { startY: event.clientY, startHeight: input.offsetHeight };
              document.body.style.cursor = "row-resize";
              document.body.style.userSelect = "none";
            }}
            onDoubleClick={() => {
              manualTextRef.current = null;
              setManualHeight(null);
            }}
            onKeyDown={event => {
              if (event.key === "ArrowUp") {
                event.preventDefault();
                resizeBy(16);
              } else if (event.key === "ArrowDown") {
                event.preventDefault();
                resizeBy(-16);
              } else if (event.key === "Home") {
                event.preventDefault();
                manualTextRef.current = text;
                setManualHeight(heightLimits().min);
              }
            }}
            className="group absolute -top-3 left-0 right-0 z-20 flex h-3 cursor-row-resize touch-none items-center justify-center outline-none focus-visible:bg-primary/15"
          >
            <span className="h-1 w-10 rounded-full bg-outline-strong opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70" />
          </div>
        )}
        {overlayActive && (
          <div
            ref={quoteLayerRef}
            aria-hidden="true"
            // Its measured height is set from the textarea rather than this
            // flex wrapper: the wrapper can include a baseline gap below the
            // actual control in narrow sidebars.
            className="pointer-events-none absolute left-px right-px top-px z-10 overflow-hidden"
          >
            <div
              className="whitespace-pre-wrap break-words px-3 py-2 text-sm text-text"
              style={{ transform: `translateY(-${scrollTop}px)` }}
            >
              {text.split(/(\r?\n)/).map((part, index) =>
                /^> ?/.test(part) ? (
                  <span
                    key={index}
                    className="-ml-1 border-l-4 border-outline-strong pl-3 text-text-mid"
                  >
                    {withHighlights(part.replace(/^> ?/, ""))}
                  </span>
                ) : (
                  <React.Fragment key={index}>{withHighlights(part)}</React.Fragment>
                ),
              )}
            </div>
          </div>
        )}
        <textarea
          {...props}
          ref={node => {
            inputRef.current = node;
            mergeRefs(forwardedRef, node);
          }}
          value={value}
          onScroll={event => {
            setScrollTop(event.currentTarget.scrollTop);
            onScroll?.(event);
          }}
          className={`block ${className} ${overlayActive ? "text-transparent caret-text" : ""}`}
          style={{ ...style, maxHeight: manualHeight === null ? maxHeight : heightLimits().max }}
        />
      </div>
    );
  },
);
QuoteTextarea.displayName = "QuoteTextarea";
